import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Autocomplete,
    Pagination,
    IconButton,
    CardMedia,
    ImageList,
    ImageListItem,
    useTheme,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Avatar,
} from "@mui/material";
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BlockIcon from '@mui/icons-material/Block';
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon from "@mui/icons-material/Inventory";

import CategoryIcon from '@mui/icons-material/Category';
import InfoIcon from '@mui/icons-material/Info';
import VerifiedIcon from '@mui/icons-material/Verified';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CloseIcon from '@mui/icons-material/Close';
import ImageWithPlaceholder, { NO_IMAGE_PLACEHOLDER } from '../components/ImageWithPlaceholder';
import { Helmet } from "react-helmet";
import { useUser } from "../context/UserContext";
import SectionLoader from "../components/SectionLoader";
import Toast from "../components/Toast";
import SettingsIcon from "@mui/icons-material/Settings";
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import ArchiveManager from "../utils/archiveManager";
import ConfirmDialog from "../components/ConfirmDialog";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import DOMPurify from "dompurify";

const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
};

const getAdminStatusInfo = (status) => {
    const s = String(status || '').trim();
    if (s === '1' || s.toLowerCase() === 'approved') {
        return { label: 'Approved', color: 'success' };
    }
    if (s === '0' || s.toLowerCase() === 'pending' || s === '3' || s.toLowerCase() === 'pendingtask') {
        return { label: 'Pending', color: 'warning' };
    }
    if (s === '2' || s.toLowerCase() === 'rejected') {
        return { label: 'Rejected', color: 'error' };
    }
    return { label: status || 'Approved', color: 'success' };
};

const renderContactDetails = (rawContact) => {
    if (!rawContact) return <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Not specified</Typography>;
    let contact = null;
    if (typeof rawContact === 'object' && rawContact !== null) {
        contact = rawContact;
    } else if (typeof rawContact === 'string') {
        const trimmed = rawContact.trim();
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                contact = JSON.parse(trimmed);
            } catch (e) {
                contact = { text: rawContact };
            }
        } else {
            contact = { text: rawContact };
        }
    }
    if (!contact) return <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Not specified</Typography>;

    if (typeof contact === 'string') {
        return (
            <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                {contact}
            </Typography>
        );
    }

    if (contact.text) {
        return (
            <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                {contact.text}
            </Typography>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={0.8} mt={0.5}>
            {contact.name && (
                <Box display="flex" alignItems="center" gap={0.8}>
                    <PersonIcon sx={{ fontSize: 16, color: '#4CA1AF' }} />
                    <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                        {contact.name}
                    </Typography>
                </Box>
            )}
            {contact.phone && (
                <Box display="flex" alignItems="center" gap={0.8}>
                    <PhoneIcon sx={{ fontSize: 16, color: '#4CA1AF' }} />
                    <Typography variant="body2" sx={{ color: '#64d2e2', fontWeight: 500 }}>
                        {contact.phone}
                    </Typography>
                </Box>
            )}
            {contact.email && (
                <Box display="flex" alignItems="center" gap={0.8}>
                    <EmailIcon sx={{ fontSize: 16, color: '#4CA1AF' }} />
                    <Typography variant="body2" sx={{ color: '#64d2e2', fontWeight: 500 }}>
                        {contact.email}
                    </Typography>
                </Box>
            )}
            {!contact.name && !contact.phone && !contact.email && (
                <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                    {Object.entries(contact).map(([k, v]) => `${k}: ${v}`).join(', ')}
                </Typography>
            )}
        </Box>
    );
};

function StatCard({ label, count, IconComponent, active = false, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                height: "100%",
                minWidth: { xs: '100%', sm: 200 },
                width: "100%",
                borderRadius: 2,
                cursor: onClick ? 'pointer' : 'default',
                background: active
                    ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.4) 0%, rgba(44, 83, 100, 0.4) 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: active ? '1px solid #4CA1AF' : '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: active ? '0 8px 32px rgba(76, 161, 175, 0.25)' : '0 4px 20px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
                    borderColor: 'rgba(76, 161, 175, 0.5)',
                },
            }}
        >
            <CardContent sx={{ minHeight: 80, p: 2 }}>
                <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                    <Box
                        sx={{
                            height: 48,
                            width: 48,
                            backgroundColor: active ? '#4CA1AF' : 'rgba(76, 161, 175, 0.15)',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            flexShrink: 0,
                            transition: 'all 0.25s ease',
                            boxShadow: active ? '0 4px 12px rgba(76, 161, 175, 0.4)' : 'none',
                        }}
                    >
                        <IconComponent sx={{ fontSize: 28, color: active ? '#FFFFFF' : '#4CA1AF' }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Typography variant="h5" fontWeight="bold" noWrap sx={{ color: '#FFFFFF' }}>
                                {count}
                            </Typography>
                            {active && (
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: 1,
                                        bgcolor: 'rgba(76, 161, 175, 0.3)',
                                        border: '1px solid rgba(76, 161, 175, 0.6)',
                                        color: '#64d2e2',
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Active
                                </Box>
                            )}
                        </Box>
                        <Typography variant="body2" noWrap sx={{ color: active ? '#FFFFFF' : 'rgba(255,255,255,0.7)', fontWeight: active ? 600 : 400 }}>
                            {label}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function GiverItems({ hideHeader = false }) {
    const { user, isSwitched, effectiveOrgId } = useUser();
    const muiTheme = useTheme();
    const isLight = muiTheme.palette.mode === 'light';
    const [sortOrder, setSortOrder] = useState('asc');
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [organization, setOrganization] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('giver-items'));
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null,
        severity: 'error'
    });

    // Filter states
    const [filterCategory, setFilterCategory] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");
    const [filterLocation, setFilterLocation] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Statistics
    const [totalItems, setTotalItems] = useState(0);
    const [availableItems, setAvailableItems] = useState(0);
    const [givenItems, setGivenItems] = useState(0);
    const [withdrawnItems, setWithdrawnItems] = useState(0);

    // View dialog
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const [openBlockDialog, setOpenBlockDialog] = useState(false);
    const [userToBlock, setUserToBlock] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/admin/';
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Categories for filter
    const categories = [
        "Electronics",
        "Books",
        "Clothes",
        "Furniture",
        "Toys",
        "Kitchen Items",
        "Sports Equipment",
        "Others"
    ];

    const statuses = ["Available", "Given", "Withdrawn"];
    const conditions = ["New", "Like New", "Used", "Fair"];

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        fetchCounts();
    }, [effectiveOrgId, organization]);

    useEffect(() => {
        fetchItems();
    }, [page, filterCategory, filterStatus, filterLocation, searchQuery, organization, effectiveOrgId, fromDate, toDate]);

    const fetchOrganizations = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}roles-organizations`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });
            } else {
                setOrganizations(data.organizations || []);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const fetchCounts = async () => {
        try {
            const token = localStorage.getItem('token');
            const targetOrg = effectiveOrgId || (organization && organization.id !== 0 ? organization.id : null);
            const requestBody = {};
            if (targetOrg) requestBody.org_id = targetOrg;

            const response = await fetch(`${API_URL}giver-items`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });
            const data = await response.json();
            if (data && Array.isArray(data.data)) {
                const all = data.data;
                setTotalItems(all.length);
                setAvailableItems(all.filter(item => item.status === 'Available').length);
                setGivenItems(all.filter(item => item.status === 'Given').length);
                setWithdrawnItems(all.filter(item => item.status === 'Withdrawn').length);
            }
        } catch (error) {
            console.error('Error fetching item counts:', error);
        }
    };

    const fetchItems = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const targetOrg = effectiveOrgId || (organization && organization.id !== 0 ? organization.id : null);

            // If user clicked Archived tab, items are displayed from archivedList locally
            if (filterStatus === "Archived") {
                setLoading(false);
                return;
            }

            // Build request body
            const requestBody = {};
            if (filterCategory) requestBody.category = filterCategory;
            if (filterStatus && filterStatus !== 'All' && filterStatus !== 'Archived') requestBody.status = filterStatus;
            if (filterLocation) requestBody.location = filterLocation;
            if (searchQuery) requestBody.search = searchQuery;
            if (targetOrg) requestBody.org_id = targetOrg;

            const response = await fetch(`${API_URL}giver-items`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });
            } else {
                let itemsData = Array.isArray(data?.data) ? data.data : [];
                if (fromDate) {
                    const from = new Date(fromDate);
                    itemsData = itemsData.filter(item => new Date(item.created_at) >= from);
                }
                if (toDate) {
                    const to = new Date(toDate);
                    to.setHours(23, 59, 59, 999);
                    itemsData = itemsData.filter(item => new Date(item.created_at) <= to);
                }

                setItems(itemsData);

                // Calculate pagination (12 items per page)
                const itemsPerPage = 12;
                setTotalPages(Math.ceil(itemsData.length / itemsPerPage));
            }
        } catch (error) {
            console.error('Error fetching items:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleViewItem = (item) => {
        setSelectedItem(item);
        setOpenViewDialog(true);
    };

    const handleViewDialogClose = () => {
        setOpenViewDialog(false);
        setSelectedItem(null);
    };

    const handleUpdateStatus = async (itemId, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}update-giver-item-status`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: itemId,
                    status: newStatus,
                    user_id: user.id
                }),
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                // 🔑 Handle invalid token
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }

                setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });

            }

            else {
                setToast({ open: true, message: 'Item status updated successfully', severity: 'success' });
                fetchItems();
            }

        } catch (error) {
            console.error('Error updating status:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
        }
    };

    const handleClearFilters = () => {
        setFilterCategory("");
        setFilterStatus("");
        setFilterLocation("");
        setSearchQuery("");
        setOrganization(null);
        setPage(1);
    };

    const handleArchiveItem = (item) => {
        setConfirmDialog({
            open: true,
            title: "Archive Item",
            message: `Are you sure you want to archive "${item.title || 'this item'}"? It will be hidden from the mobile app and can be restored anytime from the Archived tab.`,
            severity: "warning",
            onConfirm: () => {
                ArchiveManager.archiveItem('giver-items', item);
                setArchivedList(ArchiveManager.getArchivedItems('giver-items'));
                setToast({ open: true, message: 'Item archived and hidden from mobile app', severity: 'info' });
                setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
                if (openViewDialog) handleViewDialogClose();
            }
        });
    };

    const handleRestoreItem = (itemId) => {
        ArchiveManager.restoreItem('giver-items', itemId);
        setArchivedList(ArchiveManager.getArchivedItems('giver-items'));
        setToast({ open: true, message: 'Item restored to active list and mobile app', severity: 'success' });
    };

    const handleDeletePermanently = (itemId) => {
        setConfirmDialog({
            open: true,
            title: "Permanently Delete Item",
            message: "Are you sure you want to permanently delete this item? This action cannot be undone.",
            severity: "error",
            onConfirm: () => {
                ArchiveManager.deletePermanently('giver-items', itemId);
                setArchivedList(ArchiveManager.getArchivedItems('giver-items'));
                setToast({ open: true, message: 'Archived item permanently deleted', severity: 'success' });
                setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
                if (openViewDialog) handleViewDialogClose();
            }
        });
    };

    const handleConfirmDeclineItem = (itemId, itemTitle) => {
        setConfirmDialog({
            open: true,
            title: "Decline / Reject Item",
            message: `Are you sure you want to decline / reject "${itemTitle || 'this item'}"? The user will be notified of the rejection.`,
            severity: "error",
            onConfirm: () => {
                handleUpdateStatus(itemId, 'Rejected');
                setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
                if (openViewDialog) handleViewDialogClose();
            }
        });
    };

    // Filter active items excluding archived items
    const isArchivedTab = filterStatus === "Archived";
    const safeItems = Array.isArray(items) ? items : [];
    const safeArchivedList = Array.isArray(archivedList) ? archivedList : [];
    const activeItems = safeItems.filter(
        (item) => !safeArchivedList.some((a) => String(a.id) === String(item.id))
    );
    const displayedItems = isArchivedTab ? safeArchivedList : activeItems;

    // Paginate items
    const itemsPerPage = 12;
    const paginatedItems = displayedItems.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    const currentTotalPages = Math.ceil(displayedItems.length / itemsPerPage);

    const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];


    const handleBlockUnblockUser = (userId, currentBlockedStatus, userName) => {
        setUserToBlock({ userId, currentBlockedStatus, userName });
        setOpenBlockDialog(true);
    };

    const confirmBlockUnblockUser = async () => {
        try {
            const { userId, currentBlockedStatus } = userToBlock;
            const newBlockedStatus = currentBlockedStatus === 'yes' ? 'no' : 'yes';
            const token = localStorage.getItem('token');

            const response = await fetch(`${API_URL}block-unblock-user`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: userId,
                    blocked_status: newBlockedStatus
                }),
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                // 🔑 Handle invalid token
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }

                setToast({ open: true, message: data?.message || 'Failed to update user status', severity: "error" });
            } else {
                setToast({
                    open: true,
                    message: newBlockedStatus === 'yes' ? 'User blocked successfully' : 'User unblocked successfully',
                    severity: 'success'
                });
                fetchItems();
            }

            setOpenBlockDialog(false);
            setUserToBlock(null);

        } catch (error) {
            console.error('Error blocking/unblocking user:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
            setOpenBlockDialog(false);
            setUserToBlock(null);
        }
    };

    return (
        <>
            {!hideHeader && (
                <Helmet>
                    <title>Giver Items - GPN</title>
                </Helmet>
            )}

            <Box p={hideHeader ? 0 : { xs: 1, sm: 2, md: 3 }}>
                {/* Header */}
                {!hideHeader && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h5" fontWeight="bold">
                            Giver Items
                        </Typography>
                    </Box>
                )}

                                {/* Compact Unified Toolbar */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        mb: 2.5,
                        borderRadius: 2.5,
                        background: isLight ? "#FFFFFF" : "rgba(255,255,255,0.04)",
                        border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.08)",
                        backdropFilter: "blur(20px)",
                    }}
                >
                    {/* Row 1: Title, Count, Search, Sort & Actions */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5,
                            pb: 1.25,
                            borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255,255,255,0.06)",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                                Shared Items
                            </Typography>
                            <Chip
                                label={
                                    filterStatus === "Available"
                                        ? `${availableItems} Available`
                                        : filterStatus === "Given"
                                        ? `${givenItems} Given`
                                        : filterStatus === "Withdrawn"
                                        ? `${withdrawnItems} Withdrawn`
                                        : filterStatus === "Archived"
                                        ? `${archivedList.length} Archived`
                                        : `${totalItems || activeItems.length} Total`
                                }
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.2)",
                                    color: isLight ? "#0D9488" : "#64d2e2",
                                }}
                            />
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <TextField
                                size="small"
                                placeholder="Search items..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: { xs: "100%", sm: 220 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
                            />

                            <Select
                                size="small"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                displayEmpty
                                renderValue={(val) => `Sort: ${val === 'asc' ? 'A → Z' : val === 'desc' ? 'Z → A' : 'Newest'}`}
                                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
                            >
                                <MenuItem value="asc">Name: A → Z (Alphabetical)</MenuItem>
                                <MenuItem value="desc">Name: Z → A</MenuItem>
                                <MenuItem value="newest">Date: Newest First</MenuItem>
                            </Select>

                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleClearFilters}
                                sx={{ textTransform: "none", fontSize: "0.8rem", py: 0.5, px: 1.2 }}
                            >
                                Clear
                            </Button>

                            {!hideHeader && (
                                <Tooltip title="Settings">
                                    <IconButton size="small" onClick={() => setSettingsOpen(true)} sx={{ color: "#64d2e2" }}>
                                        <SettingsIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                    {/* Row 2: Status Triage Chips & Secondary Filters */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.25,
                            pt: 1.25,
                        }}
                    >
                        {/* Status Triage Chips */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            {[
                                { key: "All", label: "All", count: totalItems || activeItems.length },
                                { key: "Available", label: "Available", count: availableItems },
                                { key: "Given", label: "Given", count: givenItems },
                                { key: "Withdrawn", label: "Withdrawn", count: withdrawnItems },
                                { key: "Archived", label: "Archived", count: archivedList.length },
                            ].map((tab) => {
                                const isActive = (tab.key === "All" && (filterStatus === "All" || filterStatus === "")) || filterStatus === tab.key;
                                return (
                                    <Chip
                                        key={tab.key}
                                        label={`${tab.label} (${tab.count})`}
                                        size="small"
                                        onClick={() => {
                                            setFilterStatus(tab.key);
                                            setPage(1);
                                        }}
                                        sx={{
                                            cursor: "pointer",
                                            fontWeight: isActive ? 700 : 500,
                                            fontSize: "0.78rem",
                                            height: 26,
                                            bgcolor: isActive
                                                ? (isLight ? "#0D9488" : "#4CA1AF")
                                                : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.06)"),
                                            color: isActive ? "#FFFFFF" : (isLight ? "#475569" : "rgba(255,255,255,0.7)"),
                                            "&:hover": {
                                                bgcolor: isActive
                                                    ? (isLight ? "#0F766E" : "#3b8b98")
                                                    : (isLight ? "#E2E8F0" : "rgba(255,255,255,0.12)"),
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>

                        {/* Secondary Filters */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <Select
                                    value={filterCategory}
                                    displayEmpty
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                    renderValue={(v) => v || "All Categories"}
                                    sx={{ "& .MuiSelect-select": { py: 0.5, fontSize: "0.8rem" } }}
                                >
                                    <MenuItem value="">All Categories</MenuItem>
                                    {categories.map((cat) => (
                                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                size="small"
                                placeholder="Location..."
                                value={filterLocation}
                                onChange={(e) => setFilterLocation(e.target.value)}
                                sx={{ width: 120, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }}
                            />

                            <TextField
                                size="small"
                                type="date"
                                placeholder="From"
                                value={fromDate}
                                onChange={(e) => { if (e.target.value.length <= 10) setFromDate(e.target.value); }}
                                inputProps={{ max: "9999-12-31" }}
                                sx={{ width: 125, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.75rem" } }}
                            />

                            <TextField
                                size="small"
                                type="date"
                                placeholder="To"
                                value={toDate}
                                onChange={(e) => { if (e.target.value.length <= 10) setToDate(e.target.value); }}
                                inputProps={{ max: "9999-12-31" }}
                                sx={{ width: 125, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.75rem" } }}
                            />

                            {user?.role === 1 && !isSwitched && (
                                <Autocomplete
                                    size="small"
                                    value={organization}
                                    onChange={(event, newValue) => {
                                        setOrganization(newValue);
                                        setPage(1);
                                    }}
                                    options={orgOptions}
                                    getOptionLabel={(option) => option?.org_name || ""}
                                    renderInput={(params) => <TextField {...params} placeholder="Organization" />}
                                    sx={{ width: 150, "& .MuiInputBase-input": { py: "2px !important", fontSize: "0.8rem" } }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                />
                            )}
                        </Box>
                    </Box>
                </Paper>

                {/* Loading State */}
                {loading && <SectionLoader open={loading} />}

                {/* Items Table List View */}
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.25)',
                        bgcolor: isLight ? '#FFFFFF' : 'rgba(255, 255, 255, 0.02)',
                        boxShadow: isLight ? '0 2px 10px rgba(0, 0, 0, 0.04)' : '0 4px 20px rgba(0, 0, 0, 0.25)',
                        width: '100%',
                    }}
                >
                    <Table sx={{ minWidth: 950 }}>
                        <TableHead sx={{ bgcolor: isLight ? '#F1F5F9' : '#1a2636' }}>
                            <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 800, width: 55, color: isLight ? '#475569' : '#E2E8F0' }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 90, color: isLight ? '#475569' : '#E2E8F0' }}>Item Photo</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 220, color: isLight ? '#475569' : '#E2E8F0' }}>Item & Description</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 150, color: isLight ? '#475569' : '#E2E8F0' }}>Category & Condition</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 160, color: isLight ? '#475569' : '#E2E8F0' }}>Giver / Donor</TableCell>
                                {user?.role === 1 && (
                                    <TableCell sx={{ fontWeight: 800, minWidth: 140, color: isLight ? '#475569' : '#E2E8F0' }}>Organization</TableCell>
                                )}
                                <TableCell sx={{ fontWeight: 800, minWidth: 110, color: isLight ? '#475569' : '#E2E8F0' }}>Item Status</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 120, color: isLight ? '#475569' : '#E2E8F0' }}>Admin Status</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 110, color: isLight ? '#475569' : '#E2E8F0' }}>Date</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 800, minWidth: 160, color: isLight ? '#475569' : '#E2E8F0' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={user?.role === 1 ? 10 : 9} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary" variant="subtitle1" fontWeight={600}>
                                            {isArchivedTab ? "No archived items found." : "No items found."}
                                        </Typography>
                                        <Typography color="text.secondary" variant="caption">
                                            {isArchivedTab ? "Archived items hidden from the mobile app will show here." : "Try adjusting your filters or search query."}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedItems.map((item, idx) => {
                                    const giverName = item.app_users ? `${item.app_users.first_name || ''} ${item.app_users.last_name || ''}`.trim() : 'Anonymous Donor';
                                    const hasImages = item.images && item.images.length > 0;
                                    const plainDesc = stripHtml(item.description);

                                    return (
                                        <TableRow
                                            key={item.id}
                                            hover
                                            onClick={() => handleViewItem(item)}
                                            sx={{
                                                cursor: 'pointer',
                                                transition: 'background-color 0.15s ease',
                                                '&:hover': {
                                                    bgcolor: isLight ? '#F0FDFA !important' : 'rgba(13, 148, 136, 0.1) !important',
                                                },
                                                '& td': {
                                                    borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.06)',
                                                    py: 1.5,
                                                    color: isLight ? '#1E293B' : '#F1F5F9',
                                                }
                                            }}
                                        >
                                            <TableCell align="center" sx={{ fontWeight: 700, color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)', fontSize: '0.82rem' }}>
                                                {((page - 1) * itemsPerPage) + idx + 1}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ position: 'relative', width: 50, height: 50 }}>
                                                    <Avatar
                                                        src={hasImages ? item.images[0] : null}
                                                        variant="rounded"
                                                        sx={{ width: 50, height: 50, bgcolor: isLight ? '#F1F5F9' : 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,0,0,0.08)' }}
                                                    >
                                                        <CategoryIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
                                                    </Avatar>
                                                    {hasImages && item.images.length > 1 && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                bottom: -2,
                                                                right: -2,
                                                                bgcolor: 'rgba(0,0,0,0.75)',
                                                                color: '#FFFFFF',
                                                                fontSize: '0.62rem',
                                                                fontWeight: 700,
                                                                px: 0.5,
                                                                py: 0.1,
                                                                borderRadius: '6px',
                                                            }}
                                                        >
                                                            +{item.images.length - 1}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#F8FAFC', maxWidth: 260 }} noWrap>
                                                    {item.title}
                                                </Typography>
                                                {plainDesc && (
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 1,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        maxWidth: 260
                                                    }}>
                                                        {plainDesc}
                                                    </Typography>
                                                )}
                                                {item.location && (
                                                    <Box display="flex" alignItems="center" gap={0.4} mt={0.3}>
                                                        <LocationOnIcon sx={{ fontSize: 13, color: '#4CA1AF' }} />
                                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 240 }}>
                                                            {item.location}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
                                                    <Chip
                                                        label={item.category || 'General'}
                                                        size="small"
                                                        sx={{
                                                            height: 22,
                                                            fontWeight: 600,
                                                            fontSize: '0.72rem',
                                                            bgcolor: isLight ? '#F0FDFA' : 'rgba(76, 161, 175, 0.15)',
                                                            color: isLight ? '#0F766E' : '#64d2e2',
                                                            border: '1px solid rgba(76, 161, 175, 0.35)',
                                                        }}
                                                    />
                                                    {item.condition && (
                                                        <Chip
                                                            label={item.condition}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#0D9488', fontSize: '0.8rem', fontWeight: 700 }}>
                                                        {giverName.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Box minWidth={0}>
                                                        <Typography variant="body2" fontWeight={600} noWrap sx={{ color: isLight ? '#0F172A' : '#F8FAFC' }}>
                                                            {giverName}
                                                        </Typography>
                                                        {item.app_users?.phone && (
                                                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                                {item.app_users.phone}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            {user?.role === 1 && (
                                                <TableCell>
                                                    {item.origanisation?.org_name ? (
                                                        <Chip
                                                            label={item.origanisation.org_name}
                                                            size="small"
                                                            color="secondary"
                                                            variant="outlined"
                                                            sx={{ borderColor: '#4CA1AF', color: '#4CA1AF', fontWeight: 600, fontSize: '0.72rem' }}
                                                        />
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Chip
                                                    label={item.status || 'Available'}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.72rem',
                                                        bgcolor: item.status === 'Available' ? 'rgba(5, 150, 105, 0.12)' :
                                                            item.status === 'Given' ? 'rgba(217, 119, 6, 0.12)' : 'rgba(220, 38, 38, 0.12)',
                                                        color: item.status === 'Available' ? '#059669' :
                                                            item.status === 'Given' ? '#D97706' : '#DC2626',
                                                        border: `1px solid ${item.status === 'Available' ? '#059669' : item.status === 'Given' ? '#D97706' : '#DC2626'}`,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const info = getAdminStatusInfo(item.item_status);
                                                    return (
                                                        <Chip
                                                            label={info.label}
                                                            size="small"
                                                            color={info.color}
                                                            sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 70 }}
                                                        />
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <CalendarTodayIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewItem(item)}
                                                            sx={{ color: '#4CA1AF', '&:hover': { bgcolor: 'rgba(76, 161, 175, 0.15)' } }}
                                                        >
                                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {isArchivedTab || archivedList.some(a => String(a.id) === String(item.id)) ? (
                                                        <>
                                                            <Tooltip title="Restore to App">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleRestoreItem(item.id)}
                                                                    sx={{ color: '#10B981', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.15)' } }}
                                                                >
                                                                    <UnarchiveIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete Permanently">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeletePermanently(item.id)}
                                                                    sx={{ color: '#DC2626', '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.15)' } }}
                                                                >
                                                                    <DeleteForeverIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    ) : (
                                                        <Tooltip title="Archive Item">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleArchiveItem(item)}
                                                                sx={{ color: '#F59E0B', '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.15)' } }}
                                                            >
                                                                <ArchiveIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {!isArchivedTab && getAdminStatusInfo(item.item_status).label === 'Pending' && (
                                                        <>
                                                            <Tooltip title="Approve">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleUpdateStatus(item.id, 'Approved')}
                                                                    sx={{ color: '#059669', '&:hover': { bgcolor: 'rgba(5, 150, 105, 0.15)' } }}
                                                                >
                                                                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Decline / Reject">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleConfirmDeclineItem(item.id, item.title)}
                                                                    sx={{ color: '#EF4444', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.15)' } }}
                                                                >
                                                                    <CancelIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}

                                                    {!isArchivedTab && getAdminStatusInfo(item.item_status).label === 'Approved' && (
                                                        <Tooltip title="Decline / Reject">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleConfirmDeclineItem(item.id, item.title)}
                                                                sx={{ color: '#EF4444', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.15)' } }}
                                                            >
                                                                <CancelIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {!isArchivedTab && getAdminStatusInfo(item.item_status).label === 'Rejected' && (
                                                        <Tooltip title="Approve">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleUpdateStatus(item.id, 'Approved')}
                                                                sx={{ color: '#059669', '&:hover': { bgcolor: 'rgba(5, 150, 105, 0.15)' } }}
                                                            >
                                                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {item.app_users && (
                                                        <Tooltip title={item.app_users.blocked_status === 'yes' ? 'Unblock User' : 'Block User'}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleBlockUnblockUser(
                                                                    item.app_users.id,
                                                                    item.app_users.blocked_status,
                                                                    `${item.app_users.first_name} ${item.app_users.last_name}`
                                                                )}
                                                                sx={{
                                                                    color: item.app_users.blocked_status === 'yes' ? '#059669' : '#DC2626',
                                                                    '&:hover': { bgcolor: item.app_users.blocked_status === 'yes' ? 'rgba(5, 150, 105, 0.15)' : 'rgba(220, 38, 38, 0.15)' }
                                                                }}
                                                            >
                                                                <BlockIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {currentTotalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={3} width="100%">
                        <Pagination
                            count={currentTotalPages}
                            page={page}
                            onChange={(e, val) => setPage(val)}
                            color="primary"
                        />
                    </Box>
                )}

                {/* View Item Dialog */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleViewDialogClose}
                    fullWidth
                    maxWidth="md"
                    PaperProps={{
                        sx: {
                            background: 'linear-gradient(145deg, #15222E 0%, #1A2E3B 100%)',
                            border: '1px solid rgba(76, 161, 175, 0.35)',
                            borderRadius: '16px',
                            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.5)',
                            color: '#FFFFFF',
                        }
                    }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', pb: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <VolunteerActivismIcon sx={{ color: '#4CA1AF', fontSize: 28 }} />
                            <Box>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: '#FFFFFF', lineHeight: 1.2 }}>
                                    {selectedItem?.title || 'Shared Item Details'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                    Share & Accept • Giver Item Details
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleViewDialogClose} sx={{ color: 'rgba(255, 255, 255, 0.6)', '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.08)' } }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ py: 2.5 }}>
                        {selectedItem && (
                            <Box>
                                {/* Images with Standardized No-Image Placeholder */}
                                <Box mb={3}>
                                    {selectedItem.images && selectedItem.images.length > 0 ? (
                                        <ImageList cols={selectedItem.images.length === 1 ? 1 : Math.min(selectedItem.images.length, 2)} gap={12}>
                                            {selectedItem.images.map((image, index) => (
                                                <ImageListItem key={index}>
                                                    <ImageWithPlaceholder
                                                        src={image}
                                                        alt={`${selectedItem.title} - ${index + 1}`}
                                                        height="260px"
                                                        width="100%"
                                                        objectFit="cover"
                                                        borderRadius={2}
                                                    />
                                                </ImageListItem>
                                            ))}
                                        </ImageList>
                                    ) : (
                                        <ImageWithPlaceholder
                                            src={null}
                                            alt={selectedItem.title}
                                            height="180px"
                                            width="100%"
                                            objectFit="cover"
                                            borderRadius={2}
                                        />
                                    )}
                                </Box>

                                {/* Quick Meta Badges */}
                                <Box display="flex" flexWrap="wrap" gap={1} mb={2.5}>
                                    <Chip
                                        label={selectedItem.status || 'Available'}
                                        size="small"
                                        sx={{
                                            bgcolor: selectedItem.status === 'Available' ? 'rgba(16, 185, 129, 0.2)' :
                                                selectedItem.status === 'Given' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                            color: selectedItem.status === 'Available' ? '#10B981' :
                                                selectedItem.status === 'Given' ? '#F59E0B' : '#EF4444',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            fontWeight: 600,
                                        }}
                                    />
                                    <Chip
                                        icon={<CategoryIcon sx={{ '&&': { color: '#64d2e2', fontSize: 16 } }} />}
                                        label={selectedItem.category || 'General'}
                                        size="small"
                                        sx={{ bgcolor: 'rgba(76, 161, 175, 0.15)', color: '#64d2e2', border: '1px solid rgba(76, 161, 175, 0.3)' }}
                                    />
                                    {selectedItem.condition && (
                                        <Chip
                                            label={`Condition: ${selectedItem.condition}`}
                                            size="small"
                                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.08)', color: 'rgba(255, 255, 255, 0.9)' }}
                                        />
                                    )}
                                    {selectedItem.is_anonymous ? (
                                        <Chip label="Anonymous Giver" size="small" sx={{ bgcolor: 'rgba(147, 51, 234, 0.2)', color: '#C084FC' }} />
                                    ) : null}
                                </Box>

                                {/* Details Grid */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                                        gap: 2,
                                    }}
                                >
                                    <Box sx={{ p: 1.8, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 0.5 }}>
                                            Location
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={0.8}>
                                            <LocationOnIcon sx={{ fontSize: 18, color: '#4CA1AF' }} />
                                            <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                                                {selectedItem.location || 'Not specified'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ p: 1.8, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 0.5 }}>
                                            Delivery / Handover Method
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={0.8}>
                                            <LocalShippingIcon sx={{ fontSize: 18, color: '#4CA1AF' }} />
                                            <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                                                {selectedItem.handover_method || 'Pickup / Meetup'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' }, p: 2, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                        <Typography variant="subtitle2" sx={{ color: '#64d2e2', fontWeight: 700, mb: 1 }}>
                                            Description
                                        </Typography>
                                        <Box
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.88)',
                                                lineHeight: 1.6,
                                                fontSize: '0.92rem',
                                                '& p': { m: 0, mb: 0.8, '&:last-child': { mb: 0 } },
                                                '& strong, & b': { color: '#FFFFFF', fontWeight: 700 },
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(selectedItem.description || 'No description provided.')
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ p: 1.8, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 0.5 }}>
                                            Contact Details
                                        </Typography>
                                        {renderContactDetails(selectedItem.contact_details)}
                                    </Box>

                                    <Box sx={{ p: 1.8, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 0.5 }}>
                                            Date Posted
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={0.8} mt={0.5}>
                                            <CalendarTodayIcon sx={{ fontSize: 18, color: '#4CA1AF' }} />
                                            <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                                                {selectedItem.created_at ? new Date(selectedItem.created_at).toLocaleString() : 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1.2} flexWrap="wrap">
                            {selectedItem && (
                                <>
                                    {!isArchivedTab && !archivedList.some(a => String(a.id) === String(selectedItem.id)) ? (
                                        <>
                                            {(getAdminStatusInfo(selectedItem.item_status).label === 'Pending' || getAdminStatusInfo(selectedItem.item_status).label === 'Rejected') && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        handleUpdateStatus(selectedItem.id, 'Approved');
                                                        handleViewDialogClose();
                                                    }}
                                                    startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                                    sx={{
                                                        color: "#10B981",
                                                        borderColor: "rgba(16, 185, 129, 0.6)",
                                                        bgcolor: "rgba(16, 185, 129, 0.12)",
                                                        textTransform: "none",
                                                        borderRadius: "8px",
                                                        fontWeight: 700,
                                                        px: 1.8,
                                                        "&:hover": { borderColor: "#10B981", bgcolor: "rgba(16, 185, 129, 0.25)", color: "#FFFFFF" }
                                                    }}
                                                >
                                                    Approve
                                                </Button>
                                            )}

                                            {(getAdminStatusInfo(selectedItem.item_status).label === 'Pending' || getAdminStatusInfo(selectedItem.item_status).label === 'Approved') && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleConfirmDeclineItem(selectedItem.id, selectedItem.title)}
                                                    startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                                                    sx={{
                                                        color: "#EF4444",
                                                        borderColor: "rgba(239, 68, 68, 0.6)",
                                                        bgcolor: "rgba(239, 68, 68, 0.12)",
                                                        textTransform: "none",
                                                        borderRadius: "8px",
                                                        fontWeight: 700,
                                                        px: 1.8,
                                                        "&:hover": { borderColor: "#EF4444", bgcolor: "rgba(239, 68, 68, 0.25)", color: "#FFFFFF" }
                                                    }}
                                                >
                                                    Decline
                                                </Button>
                                            )}

                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleArchiveItem(selectedItem)}
                                                startIcon={<ArchiveIcon sx={{ fontSize: 16 }} />}
                                                sx={{
                                                    color: "#ffb74d",
                                                    borderColor: "rgba(255, 183, 77, 0.6)",
                                                    bgcolor: "rgba(255, 183, 77, 0.12)",
                                                    textTransform: "none",
                                                    borderRadius: "8px",
                                                    fontWeight: 700,
                                                    px: 1.8,
                                                    "&:hover": { borderColor: "#ffb74d", bgcolor: "rgba(255, 183, 77, 0.25)", color: "#FFFFFF" }
                                                }}
                                            >
                                                Archive
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    handleRestoreItem(selectedItem.id);
                                                    handleViewDialogClose();
                                                }}
                                                startIcon={<UnarchiveIcon sx={{ fontSize: 16 }} />}
                                                sx={{
                                                    color: "#4CAF50",
                                                    borderColor: "rgba(76, 175, 80, 0.6)",
                                                    bgcolor: "rgba(76, 175, 80, 0.12)",
                                                    textTransform: "none",
                                                    borderRadius: "8px",
                                                    fontWeight: 700,
                                                    px: 1.8,
                                                    "&:hover": { borderColor: "#4CAF50", bgcolor: "rgba(76, 175, 80, 0.25)", color: "#FFFFFF" }
                                                }}
                                            >
                                                Restore
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleDeletePermanently(selectedItem.id)}
                                                startIcon={<DeleteForeverIcon sx={{ fontSize: 16 }} />}
                                                sx={{
                                                    color: "#EF4444",
                                                    borderColor: "rgba(239, 68, 68, 0.6)",
                                                    bgcolor: "rgba(239, 68, 68, 0.12)",
                                                    textTransform: "none",
                                                    borderRadius: "8px",
                                                    fontWeight: 700,
                                                    px: 1.8,
                                                    "&:hover": { borderColor: "#EF4444", bgcolor: "rgba(239, 68, 68, 0.25)", color: "#FFFFFF" }
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </Box>
                        <Button
                            onClick={handleViewDialogClose}
                            variant="outlined"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.8)',
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                borderRadius: '8px',
                                '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.05)' }
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>


                            {/* Block/Unblock Confirmation Dialog */}
                <Dialog
                    open={openBlockDialog}
                    onClose={() => setOpenBlockDialog(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>
                        <Typography variant="h6" fontWeight="bold">
                            Confirm {userToBlock?.currentBlockedStatus === 'yes' ? 'Unblock' : 'Block'} User
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" gutterBottom>
                            Are you sure you want to {userToBlock?.currentBlockedStatus === 'yes' ? 'unblock' : 'block'} <strong>{userToBlock?.userName}</strong>?
                        </Typography>
                        {userToBlock?.currentBlockedStatus !== 'yes' && (
                            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                ⚠️ This will prevent the user from accessing Share & Accept feature on the platform.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenBlockDialog(false)} color="inherit">
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmBlockUnblockUser}
                            variant="contained"
                            color={userToBlock?.currentBlockedStatus === 'yes' ? 'success' : 'error'}
                        >
                            {userToBlock?.currentBlockedStatus === 'yes' ? 'Unblock' : 'Block'}
                        </Button>
                    </DialogActions>
                </Dialog>



                {/* Toast Notifications */}
                <Toast toast={toast} setToast={setToast} />
            </Box>
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
                severity={confirmDialog.severity || "error"}
            />
            <ModuleSettingsModal
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                moduleKey="giverItems"
                moduleTitle="Giver Items"
            />
        </>
    );
}
